import { useEffect, useState } from 'react';

import type { AudioDevicesT } from 'client/services/audio';

import { getAudioClientService } from 'services';

import { useGlobalState } from 'utils';

type UseDevicesT = AudioDevicesT & {
  refresh: () => void;
};

export function useDevices(): UseDevicesT {
  const [, setBlockedAudioDeviceAccess] = useGlobalState(
    'ui.blockedAudioDeviceAccess',
  );

  const [res, setRes] = useState<UseDevicesT>({
    input: [],
    output: [],
    refresh: () => {
      void (async () => {
        try {
          const audio = await getAudioClientService();
          const { input, output } = await audio.getAudioDevices();
          setRes((prev) => ({ ...prev, input, output }));
        } catch (error) {
          if (error instanceof Error && error.name === 'NotAllowedError') {
            setBlockedAudioDeviceAccess(true);
          } else {
            throw error;
          }
        }
      })();
    },
  });

  // NOTE: The way we update `res`, its .refresh() method never changes after it
  // is first set.
  const { refresh } = res;

  useEffect(() => {
    refresh();

    const cleanupPromise = (async () => {
      const audio = await getAudioClientService();
      return audio.emitter.addListener((event) => {
        if (event === 'AUDIO_DEVICES_CHANGE') refresh();
      });
    })();

    return () => {
      void cleanupPromise.then((cleanup) => {
        cleanup();
      });
    };
  }, [refresh]);

  return res;
}
